/**
 * Publisher - Base class for publish/subscribe pattern with state management
 *
 * Provides foundation for event-driven architecture. Publisher maintains an internal
 * value and notifies subscribers when it changes. Used by services like Locale, System,
 * and Countdown to broadcast state changes.
 *
 * PUBLIC API NOTE: Public methods auto-inject caller context as first argument.
 * JSDoc @param lists show only user-provided parameters (what you actually pass).
 *
 * PATTERN: Extends Class to add pub/sub capabilities:
 * - Maintains internal value (this.data.value)
 * - Tracks subscribers (this.subscriptions array)
 * - Publishes changes to all subscribers
 * - Automatic cleanup (dead instances auto-unsubscribed)
 *
 * KEY FEATURES:
 * - get() - Returns current value, optionally subscribes for updates
 * - publish() - Notifies all subscribers of value changes
 * - update() - Patches value and publishes changes
 * - unsubscribe() - Remove subscriptions
 * - Auto-cleanup of dead instances
 *
 * MEMORY: Automatically removes subscriptions for destroyed instances (checks instance.alive)
 *
 * @class Publisher
 * @extends Class
 *
 * @example
 * // Extend Publisher for custom service
 * const MyService = Publisher.inherit({
 *     namespace: 'MyService',
 *     singleton: true,
 *     constructor() {
 *         // Set initial value
 *         this.assign(this.data.value, {
 *             status: 'ready'
 *         });
 *     }
 * });
 *
 * @example
 * // Subscribe to publisher
 * this.MyService.get({
 *     subscribe: function(value) {
 *         // Called when value changes
 *         this.data.serviceStatus = value.status;
 *     },
 *     fireImmediately: true
 * });
 *
 * @example
 * // Update publisher value (notifies subscribers)
 * MyService.update({
 *     status: 'processing'
 * });
 */
define("Publisher", [`Class`, `module`], (Super, { id: namespace }) =>
	Super.inherit({
		namespace,
		prepare(setup) {
			const { data } = this;
			const publishedValue = null; // used to compare when deciding to publish updates
			const value = {};

			this.assign(data, { publishedValue, value });
			this.defineProperty(`subscriptions`, { value: [] }); // make the assignment constant

			// NOTE: inheriting classes should have an initial value ready by the time constructor() is complete
		},
		api: {
			/**
			 * Get current value and optionally subscribe to future changes.
			 *
			 * @method Publisher#get
			 * @param {Object} [options] - Subscription options
			 * @param {string} [options.dataProperty] - Auto-sync to this.data[property]
			 * @param {Function} [options.derive] - Transform the value before it reaches the handler and return value
			 * @param {Function} [options.subscribe] - Callback on value changes
			 * @param {boolean} [options.fireImmediately=false] - Call subscribe handler immediately
			 * @param {boolean} [options.reconcile=false] - Call this.reconcile() on changes
			 * @param {boolean} [options.reconcileImmediately=false] - Call this.reconcile() immediately
			 * @returns {*} Current publisher value, or derived value if options.derive is provided
			 */
			get: {
				public: true,
				defaults: {
					dataProperty: null,
					derive: null,
					fireImmediately: false,
					reconcile: false,
					reconcileImmediately: false,
				},
				value(context, options) {
					const { data, subscriptions } = this;
					const { value } = data;
					const instance = context; // used to keep track of the requesting instance
					const { dataProperty, derive, subscribe: handler } = options;
					const hasDataProperty = this.isString(dataProperty, 1);
					const hasHandler = this.isFunction(handler);
					const hasDerive = this.isFunction(derive);

					if (hasHandler || hasDataProperty) {
						const { fireImmediately, reconcile, dataProperty } = options;

						if (reconcile && !this.isFunction(instance.reconcile)) {
							const { namespace } = instance;
							throw new Error(`Publisher.subscribe(options.reconcile: true) requires ${namespace}.reconcile()`);
						}

						const deriveResult = null; // will be used to cache the transformed result for comparison on future updates
						const subscription = { dataProperty, derive, deriveResult, handler, instance, reconcile };
						subscriptions.push(subscription);

						if (fireImmediately) {
							const { reconcileImmediately } = options;

							if (hasDataProperty) {
								instance.data[dataProperty] = this.clone(value);
							}

							if (hasHandler) {
								const clonedValue = this.clone(value);
								const handlerValue = hasDerive ? derive(clonedValue) : clonedValue;

								if (hasDerive) {
									subscription.deriveResult = this.clone(handlerValue);
								}

								handler.call(instance, handlerValue);
							}

							if (reconcileImmediately) {
								instance.reconcile();
							}
						}
					}

					const protectedValue = this.clone(value);
					const result = hasDerive ? derive(protectedValue) : protectedValue;

					return result;
				},
			},

			/**
			 * A private function that checks the internal value for changes, publishing to the subscribers if any are found.
			 * @method Publisher#publish
			 */
			publish: {
				// private function for notifying all subscriptions that an update occurred
				value() {
					const { data, subscriptions } = this;
					const { publishedValue, value } = data;
					const updated = !this.isEqual(value, publishedValue);

					if (updated) {
						// use extract() to iterate over the list of subscribers, so entries can return true to be removed
						this.extract(subscriptions, (subscription) => {
							const { derive, dataProperty, handler, instance, reconcile } = subscription;

							if (!instance.alive) {
								// skip, remove from subscribers
								return true;
							}

							const hasDerive = this.isFunction(derive);
							const hasHandler = this.isFunction(handler);
							let skipReconcile = !reconcile;
							let skipHandler = !hasHandler;
							let handlerResult;

							if (hasDerive) {
								const deriveResult = derive(this.clone(value));

								if (this.isEqual(deriveResult, subscription.deriveResult)) {
									skipHandler = true;
								} else {
									subscription.deriveResult = this.clone(deriveResult);
									handlerResult = deriveResult;
								}
							}

							this.defer({
								done: true,
								then: () => {
									if (this.isString(dataProperty, 1)) {
										instance.data[dataProperty] = this.clone(value);
									} else if (skipHandler) {
										// no handler and no dataProperty sync — no reason to reconcile
										skipReconcile = true;
									}

									if (hasHandler && !skipHandler) {
										if (!hasDerive) {
											handlerResult = this.clone(value);
										}

										handler.call(instance, handlerResult);
									}

									if (!skipReconcile) {
										// trigger the requesting instance's reconcile()
										instance.reconcile();
									}
								},
							});
						});
					}
				},
			},

			/**
			 * Apply changes to the internal value and call this.publish()
			 * @method Publisher#update
			 * @param {Object} options - Properties to patch the internal value with
			 */
			update: {
				value(options) {
					const { data } = this;
					const { value } = data;

					this.assign(value, options);
					this.publish();
				},
			},

			/**
			 * Remove subscriptions matching criteria.
			 *
			 * @method Publisher#unsubscribe
			 * @param {Object} [options] - Filter options (defaults to unsubscribe caller)
			 * @param {Object} [options.instance] - Unsubscribe this instance
			 * @param {Function} [options.handler] - Unsubscribe this handler
			 */
			unsubscribe: {
				public: true,
				defaults: {},
				value(context, options) {
					const instance = context;

					this.defaults(options, { instance });
					this.extract(this.subscriptions, options);
				},
			},
		},
	}));

//# sourceMappingURL=Publisher.js.map